/** User menu — avatar dropdown for the navbar. Shows different items based on role. */
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Shield, Settings as Cog } from "lucide-react";
import { Avatar } from "../../components/ui/Avatar";
import { useAuth, userIsStaff } from "./AuthContext";
import { cn } from "../../lib/cn";

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function UserMenu({ className }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;

  const doLogout = async () => {
    await logout();
    setOpen(false);
    nav("/", { replace: true });
  };

  return (
    <div className={cn("relative", className)} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open user menu"
        data-testid="user-menu-btn"
        className="inline-flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-mauve focus:ring-offset-2 focus:ring-offset-bg"
      >
        <Avatar size="md" initials={initials(user.name || user.email)} seed={user.email} />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 min-w-[240px] rounded-2xl border border-line-2 bg-glass-2 backdrop-blur-xl shadow-float p-1.5 z-50"
          data-testid="user-menu"
        >
          <div className="px-3 py-2.5 border-b border-line mb-1">
            <div className="font-semibold text-[14px] text-text truncate" data-testid="user-menu-name">{user.name || "Student"}</div>
            <div className="text-[12px] text-text-faint truncate" data-testid="user-menu-email">{user.email}</div>
            <div className="text-[10px] mt-1 font-mono uppercase tracking-[0.16em] text-mauve">{user.role}</div>
          </div>
          <Link
            to="/dashboard"
            onClick={() => setOpen(false)}
            data-testid="user-menu-dashboard"
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-surface-2 transition-colors text-[13.5px]"
          >
            <LayoutDashboard size={14} className="text-mauve" /> My dashboard
          </Link>
          {userIsStaff(user) && (
            <Link
              to="/admin"
              onClick={() => setOpen(false)}
              data-testid="user-menu-admin"
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-surface-2 transition-colors text-[13.5px]"
            >
              <Shield size={14} className="text-mauve" /> Admin console
            </Link>
          )}
          <Link
            to="/dashboard/settings"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-surface-2 transition-colors text-[13.5px]"
          >
            <Cog size={14} className="text-mauve" /> Account settings
          </Link>
          <button
            type="button"
            onClick={doLogout}
            data-testid="user-menu-logout"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl hover:bg-surface-2 transition-colors text-[13.5px] text-left mt-1 border-t border-line"
          >
            <LogOut size={14} className="text-mauve" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
